// src/pages/ServiceDetailPage.js

import React, { useContext, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { serviceService } from '../api';
import useApi from '../hooks/useApi';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';
import SocialShare from '../components/common/SocialShare';

// Styled components
const ServiceSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Breadcrumbs = styled.div`
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 0.5rem;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceContent = styled.div`
`;

const ServiceImage = styled.div`
  width: 100%;
  height: 400px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const ServiceTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ServiceDescription = styled.div`
  color: ${props => props.theme.text};
  line-height: 1.8;
  margin-bottom: 2rem;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  h2 {
    font-size: 1.8rem;
    margin: 2rem 0 1rem;
    color: ${props => props.theme.primary};
  }
  
  h3 {
    font-size: 1.5rem;
    margin: 1.8rem 0 1rem;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const TechTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 2rem 0;
`;

const TechTag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  background: ${props => props.theme.primary}20;
  color: ${props => props.theme.primary};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const ServiceSidebar = styled.div`
`;

const SidebarCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const SidebarTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.primary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -8px;
    width: 50px;
    height: 2px;
    background-color: ${props => props.theme.accent};
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    padding: 0.8rem 0;
    border-bottom: 1px solid ${props => props.theme.text}10;
    color: ${props => props.theme.text};
    display: flex;
    align-items: flex-start;
    
    &:last-child {
      border-bottom: none;
    }
    
    i {
      color: ${props => props.theme.primary};
      margin-right: 1rem;
      margin-top: 0.2rem;
    }
  }
`;

const PriceTag = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.primary};
  margin-bottom: 1rem;
  
  .unit {
    font-size: 1rem;
    opacity: 0.7;
  }
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  
  // Charger les détails du service
  const { 
    data: service, 
    loading: serviceLoading, 
    error: serviceError 
  } = useApi(() => serviceService.getById(serviceSlug), [serviceSlug]);
  
  // Mise à jour du titre de la page
  useEffect(() => {
    if (service) {
      document.title = `${service.name} | PropentaTech`;
    }
    return () => {
      document.title = 'PropentaTech';
    };
  }, [service]);
  
  if (serviceLoading) {
    return (
      <ServiceSection>
        <Container>
          <LoadingIndicator>
            <svg width="50" height="50" viewBox="0 0 50 50">
              <circle 
                cx="25" 
                cy="25" 
                r="20" 
                fill="none" 
                stroke={theme.primary} 
                strokeWidth="3" 
                strokeLinecap="round"
              >
                <animateTransform 
                  attributeName="transform" 
                  type="rotate" 
                  from="0 25 25" 
                  to="360 25 25" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
                <animate 
                  attributeName="stroke-dasharray" 
                  from="1, 150" 
                  to="90, 150" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
                <animate 
                  attributeName="stroke-dashoffset" 
                  from="0" 
                  to="-35" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </LoadingIndicator>
        </Container>
      </ServiceSection>
    );
  }
  
  if (serviceError) {
    return (
      <ServiceSection>
        <Container>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', color: theme.error, marginBottom: '1rem' }}>
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <h2 style={{ fontSize: '1.8rem', color: theme.text, marginBottom: '1.5rem' }}>{serviceError}</h2>
              <Button variant="primary" to="/services">
                <i className="fas fa-arrow-left"></i> {t('backToServices')}
              </Button>
            </motion.div>
          </div>
        </Container>
      </ServiceSection>
    );
  }
  
  if (!service) return null;
  
  // Préparer les fonctionnalités pour l'affichage
  const features = service.description.split('\n').filter(line => line.trim().startsWith('- ')).map(line => line.replace('- ', ''));
  
  // Préparer les technologies pour l'affichage
  const technologies = service.technologies ? service.technologies.split(',').map(tech => tech.trim()) : [];
  
  return (
    <ServiceSection>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Breadcrumbs theme={theme}>
            <Link to="/">{t('home')}</Link> <span>/</span> 
            <Link to="/services">{t('services')}</Link> <span>/</span> 
            <span>{service.name}</span>
          </Breadcrumbs>
          
          <ServiceTitle theme={theme}>{service.name}</ServiceTitle>
          
          <ServiceGrid>
            <ServiceContent>
              {service.image && (
                <ServiceImage src={service.image} />
              )}
              
              <SocialShare 
                title={service.name}
                hashtags={['PropentaTech', 'Services', service.name.replace(/\s+/g, '')]}
              />
              
              <ServiceDescription theme={theme} dangerouslySetInnerHTML={{ __html: service.description }} />
              
              {technologies.length > 0 && (
                <>
                  <h2 style={{ color: theme.primary, marginBottom: '1rem' }}>{t('technologiesUsed')}</h2>
                  <TechTagsContainer>
                    {technologies.map((tech, index) => (
                      <TechTag key={index} theme={theme}>{tech}</TechTag>
                    ))}
                  </TechTagsContainer>
                </>
              )}
              
              <div style={{ marginTop: '2rem' }}>
                <Button
                  variant="primary"
                  size="large"
                  to="/contact"
                >
                  <i className="fas fa-paper-plane" style={{ marginRight: '8px' }}></i>
                  {t('getQuote')}
                </Button>
              </div>
            </ServiceContent>
            
            <ServiceSidebar>
              {service.price && (
                <SidebarCard theme={theme}>
                  <SidebarTitle theme={theme}>{t('pricing')}</SidebarTitle>
                  <PriceTag theme={theme}>
                    {service.price} <span className="unit">FCFA</span>
                  </PriceTag>
                  <p style={{ color: theme.text, opacity: 0.8, marginBottom: '1.5rem' }}>
                    {t('pricingDescription')}
                  </p>
                  <Button
                    variant="primary"
                    size="large"
                    style={{ width: '100%' }}
                    to="/contact"
                  >
                    {t('contactUs')}
                  </Button>
                </SidebarCard>
              )}
              
              {features.length > 0 && (
                <SidebarCard theme={theme}>
                  <SidebarTitle theme={theme}>{t('keyFeatures')}</SidebarTitle>
                  <FeaturesList theme={theme}>
                    {features.map((feature, index) => (
                      <li key={index}>
                        <i className="fas fa-check-circle"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </FeaturesList>
                </SidebarCard>
              )}
              
              <SidebarCard theme={theme}>
                <SidebarTitle theme={theme}>{t('needHelp')}</SidebarTitle>
                <p style={{ color: theme.text, marginBottom: '1.5rem' }}>
                  {t('needHelpDescription')}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', color: theme.text }}>
                  <i className="fas fa-phone" style={{ color: theme.primary, marginRight: '1rem', fontSize: '1.5rem' }}></i>
                  <div>
                    <div>+237 657 097 369</div>
                    <div>+237 690 232 120</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', color: theme.text }}>
                  <i className="fas fa-envelope" style={{ color: theme.primary, marginRight: '1rem', fontSize: '1.5rem' }}></i>
                  <div>propentatech@gmail.com</div>
                </div>
              </SidebarCard>
            </ServiceSidebar>
          </ServiceGrid>
        </motion.div>
      </Container>
    </ServiceSection>
  );
};

export default ServiceDetailPage;